<template>
  <div class="aks">
    <!-- Loading -->
    <div v-if="loading" class="aks-loading"><div class="aks-spinner"></div></div>

    <!-- Load error -->
    <div v-if="loadError && !loading" class="aks-error">
      <p class="aks-error-title">Could not load API keys</p>
      <p class="aks-error-msg">{{ loadError }}</p>
      <button class="aks-btn aks-btn-outline" type="button" @click="fetchAll">Retry</button>
    </div>

    <template v-if="!loading && !loadError">
      <!-- Page header -->
      <div class="aks-page-head">
        <div class="aks-page-head-text">
          <h1 class="aks-title">API Keys</h1>
          <p class="aks-subtitle">Authenticate requests to the SentryKit REST API from your own scripts, apps, or integrations.</p>
        </div>
        <button v-if="!showForm" class="aks-btn aks-btn-primary" type="button" @click="openForm">
          <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" style="margin-right:6px;vertical-align:-1px"><path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/></svg>
          Create API key
        </button>
      </div>

      <!-- Usage hint banner -->
      <div class="aks-hint-banner" role="note">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
        <div>
          Use your key in the <code class="aks-code">Authorization: Bearer sk_live_…</code> header when calling
          <code class="aks-code">https://api.sentrykit.com/api/external/v1/</code>.
        </div>
      </div>

      <!-- Create form -->
      <div v-if="showForm" class="aks-card aks-form-card">
        <h3 class="aks-form-title">Create API key</h3>
        <div v-if="formError" class="aks-alert-error" role="alert">{{ formError }}</div>

        <div class="aks-field">
          <label class="aks-field-label">KEY NAME</label>
          <input
            v-model="formName"
            type="text"
            class="aks-input"
            placeholder="e.g. Production script, Zapier integration"
            maxlength="100"
            autocomplete="off"
          />
          <p class="aks-field-hint">Optional — helps you identify this key later.</p>
        </div>

        <div class="aks-form-actions">
          <button class="aks-btn aks-btn-outline" type="button" @click="closeForm" :disabled="submitting">Cancel</button>
          <button class="aks-btn aks-btn-primary" type="button" @click="createKey" :disabled="submitting" :class="{ 'aks-btn-loading': submitting }">
            {{ submitting ? 'Creating…' : 'Create key' }}
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!apiKeys.length && !showForm" class="aks-empty">
        <svg class="aks-empty-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="14" width="40" height="24" rx="3" stroke="currentColor" stroke-width="2"/>
          <circle cx="17" cy="26" r="5" stroke="currentColor" stroke-width="2"/>
          <path d="M22 26h10M32 26l4 4M32 26l4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <circle cx="17" cy="26" r="2" fill="currentColor" opacity="0.35"/>
        </svg>
        <p class="aks-empty-title">No API keys yet</p>
        <p class="aks-empty-sub">Create a key to authenticate your scripts and integrations with the SentryKit REST API.</p>
        <button class="aks-btn aks-btn-primary" type="button" @click="openForm">
          <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor" style="margin-right:5px;vertical-align:-1px"><path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/></svg>
          Create API key
        </button>
      </div>

      <!-- Keys table -->
      <div v-if="apiKeys.length" class="aks-card">
        <table class="aks-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Key</th>
              <th>Scopes</th>
              <th>Last used</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="key in apiKeys" :key="key.id" :class="{ 'aks-row-revoked': key.revoked_at }">
              <td class="aks-cell-name">
                <span class="aks-key-name">{{ key.name || 'Unnamed key' }}</span>
                <span v-if="key.revoked_at" class="aks-badge-revoked">Revoked</span>
              </td>
              <td class="aks-cell-key">
                <code class="aks-prefix">{{ key.token_prefix }}<span class="aks-prefix-dots">••••</span></code>
              </td>
              <td class="aks-cell-scopes">
                <span v-for="s in key.scopes" :key="s" class="aks-scope-chip">{{ s }}</span>
              </td>
              <td class="aks-cell-meta">{{ key.last_used_at ? formatDate(key.last_used_at) : 'Never' }}</td>
              <td class="aks-cell-meta">{{ formatDate(key.created_at) }}</td>
              <td class="aks-cell-action">
                <button
                  v-if="!key.revoked_at"
                  type="button"
                  class="aks-revoke-btn"
                  :disabled="revokingId === key.id"
                  @click="confirmRevoke(key.id)"
                >
                  {{ revokingId === key.id ? 'Revoking…' : 'Revoke' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ===== Token modal (show-once) ===== -->
    <div v-if="tokenModal.show" class="aks-overlay" role="dialog" aria-modal="true" @click.self="closeTokenModal">
      <div class="aks-modal">
        <div class="aks-modal-icon-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
        </div>
        <h3 class="aks-modal-title">API key created — save it now</h3>
        <p class="aks-modal-body">
          <strong>Copy this key now — it won't be shown again.</strong>
          Use it as <code class="aks-code">Authorization: Bearer &lt;key&gt;</code> in your API requests.
        </p>
        <div class="aks-token-box">
          <code class="aks-token-code">{{ tokenModal.token }}</code>
          <button type="button" class="aks-copy-btn" @click="copyToClipboard(tokenModal.token)">
            <svg v-if="!copied" width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/><path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/></svg>
            {{ copied ? '✓ Copied!' : 'Copy' }}
          </button>
        </div>
        <div class="aks-example-block">
          <p class="aks-example-label">Example request</p>
          <code class="aks-example-code">curl -H "Authorization: Bearer sk_live_…" \<br>&nbsp;&nbsp;https://api.sentrykit.com/api/external/v1/alerts</code>
        </div>
        <button type="button" class="aks-btn aks-btn-primary aks-modal-done-btn" @click="closeTokenModal">Done, I've copied it</button>
      </div>
    </div>

    <!-- ===== Revoke confirm modal ===== -->
    <div v-if="revokeConfirmId != null" class="aks-overlay" role="dialog" aria-modal="true" @click.self="revokeConfirmId = null">
      <div class="aks-modal aks-modal-sm">
        <div class="aks-modal-icon-wrap aks-modal-icon-danger">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <h3 class="aks-modal-title">Revoke API key?</h3>
        <p class="aks-modal-body">This immediately stops the key from authenticating. Any integration using it will break. <strong>There is no undo</strong> — you'll need to create a new key.</p>
        <div class="aks-modal-btns">
          <button type="button" class="aks-btn aks-btn-outline" @click="revokeConfirmId = null" :disabled="revokingId != null">Cancel</button>
          <button type="button" class="aks-btn aks-btn-danger" :disabled="revokingId != null" :class="{ 'aks-btn-loading': revokingId != null }" @click="revokeKey(revokeConfirmId)">
            {{ revokingId != null ? 'Revoking…' : 'Revoke key' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ApiKeysSettings',

  data: function () {
    return {
      loading: false,
      loadError: null,
      apiKeys: [],

      showForm: false,
      formName: '',
      submitting: false,
      formError: null,

      tokenModal: { show: false, token: '' },
      copied: false,

      revokeConfirmId: null,
      revokingId: null
    }
  },

  mounted: function () {
    this.fetchAll()
  },

  methods: {
    fetchAll: function () {
      this.loading = true
      this.loadError = null
      var self = this
      this.$http.get('/api/v1/api_keys').then(function (res) {
        self.loading = false
        self.apiKeys = (res.body || {}).api_keys || []
      }).catch(function (err) {
        self.loading = false
        var body = (err && err.body) || {}
        var msg = body.message
        self.loadError = Array.isArray(msg) ? msg.join(', ') : (msg || 'Failed to load API keys')
      })
    },

    openForm: function () {
      this.showForm = true
      this.formName = ''
      this.formError = null
    },

    closeForm: function () {
      this.showForm = false
      this.formError = null
    },

    createKey: function () {
      this.formError = null
      this.submitting = true
      var self = this
      var payload = {
        api_key: {
          name: this.formName.trim() || 'API key',
          scopes: ['read']
        }
      }
      this.$http.post('/api/v1/api_keys', payload).then(function (res) {
        self.submitting = false
        var key = (res.body || {}).api_key || {}
        var token = key.token || ''
        delete key.token
        self.apiKeys.unshift(key)
        self.closeForm()
        self.tokenModal = { show: true, token: token }
      }).catch(function (err) {
        self.submitting = false
        var body = (err && err.body) || {}
        var msg = body.message
        self.formError = Array.isArray(msg) ? msg.join(', ') : (msg || 'Failed to create API key')
      })
    },

    confirmRevoke: function (id) {
      this.revokeConfirmId = id
    },

    revokeKey: function (id) {
      this.revokingId = id
      var self = this
      this.$http.delete('/api/v1/api_keys/' + id).then(function () {
        self.revokingId = null
        self.revokeConfirmId = null
        var now = new Date().toISOString()
        var idx = self.apiKeys.findIndex(function (k) { return k.id === id })
        if (idx > -1) {
          var updated = Object.assign({}, self.apiKeys[idx], { revoked_at: now })
          self.$set(self.apiKeys, idx, updated)
        }
      }).catch(function (err) {
        self.revokingId = null
        var body = (err && err.body) || {}
        alert(Array.isArray(body.message) ? body.message.join(', ') : (body.message || 'Failed to revoke key'))
      })
    },

    closeTokenModal: function () {
      this.tokenModal = { show: false, token: '' }
      this.copied = false
    },

    copyToClipboard: function (text) {
      var self = this
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(function () {
          self.copied = true
          setTimeout(function () { self.copied = false }, 2500)
        })
      } else {
        var ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        self.copied = true
        setTimeout(function () { self.copied = false }, 2500)
      }
    },

    formatDate: function (iso) {
      if (!iso) return '—'
      try {
        var d = new Date(iso)
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      } catch (e) {
        return iso
      }
    }
  }
}
</script>

<style scoped>
/* ── Layout ── */
.aks {
  font-family: inherit;
}

.aks-page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.aks-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.025em;
  color: var(--primary-navy);
  margin: 0 0 4px;
}

.aks-subtitle {
  font-size: 13px;
  color: rgb(100, 116, 136);
  margin: 0;
  max-width: 520px;
}

/* ── Loading / error ── */
.aks-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
}

.aks-error {
  text-align: center;
  padding: 40px 20px;
}

.aks-error-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-navy);
  margin: 0 0 6px;
}

.aks-error-msg {
  font-size: 13px;
  color: rgb(120, 130, 145);
  margin: 0 0 16px;
}

/* ── Spinner ── */
.aks-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgb(220, 230, 240);
  border-top-color: var(--primary-navy);
  border-radius: 50%;
  animation: aks-spin 0.7s linear infinite;
}

@keyframes aks-spin {
  to { transform: rotate(360deg); }
}

/* ── Hint banner ── */
.aks-hint-banner {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  background: rgb(248, 251, 255);
  border: 1px solid rgb(218, 232, 248);
  border-radius: 8px;
  padding: 11px 14px;
  font-size: 12.5px;
  color: rgb(60, 80, 108);
  margin-bottom: 20px;
  line-height: 1.55;
}

.aks-code {
  font-family: 'SF Mono', 'Fira Mono', monospace;
  font-size: 11.5px;
  background: rgba(18, 46, 82, 0.07);
  padding: 1px 5px;
  border-radius: 4px;
}

/* ── Buttons ── */
.aks-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 7px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.13s, box-shadow 0.13s, opacity 0.13s;
  white-space: nowrap;
  line-height: 1.4;
}

.aks-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.aks-btn-primary {
  background: var(--primary-navy);
  color: #fff;
  border-color: var(--primary-navy);
}

.aks-btn-primary:hover:not(:disabled) {
  background: #1a2f52;
}

.aks-btn-outline {
  background: #fff;
  color: var(--primary-navy);
  border-color: rgb(210, 220, 232);
}

.aks-btn-outline:hover:not(:disabled) {
  background: rgb(245, 248, 252);
}

.aks-btn-danger {
  background: #dc2626;
  color: #fff;
  border-color: #dc2626;
}

.aks-btn-danger:hover:not(:disabled) {
  background: #b91c1c;
}

.aks-btn-loading {
  opacity: 0.65;
  cursor: wait;
}

/* ── Card ── */
.aks-card {
  background: #fff;
  border: 1px solid rgb(228, 235, 244);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
}

.aks-form-card {
  padding: 20px 24px 24px;
  margin-bottom: 20px;
}

.aks-form-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-navy);
  margin: 0 0 18px;
}

/* ── Form fields ── */
.aks-field {
  margin-bottom: 16px;
}

.aks-field-label {
  display: block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: rgb(100, 116, 136);
  margin-bottom: 6px;
}

.aks-input {
  width: 100%;
  max-width: 420px;
  padding: 9px 12px;
  font-size: 13px;
  border: 1px solid rgb(210, 220, 232);
  border-radius: 7px;
  color: var(--primary-navy);
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.13s, box-shadow 0.13s;
}

.aks-input:focus {
  outline: none;
  border-color: var(--primary-navy);
  box-shadow: 0 0 0 3px rgba(18, 46, 82, 0.08);
}

.aks-field-hint {
  font-size: 12px;
  color: rgb(130, 142, 158);
  margin: 5px 0 0;
}

.aks-alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 7px;
  padding: 10px 14px;
  font-size: 13px;
  color: #991b1b;
  margin-bottom: 16px;
}

.aks-form-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

/* ── Empty state ── */
.aks-empty {
  text-align: center;
  padding: 52px 20px;
  background: #fff;
  border: 1px dashed rgb(210, 220, 235);
  border-radius: 10px;
}

.aks-empty-icon {
  width: 48px;
  height: 48px;
  color: rgb(180, 195, 212);
  margin: 0 auto 14px;
  display: block;
}

.aks-empty-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-navy);
  margin: 0 0 6px;
}

.aks-empty-sub {
  font-size: 13px;
  color: rgb(120, 135, 155);
  margin: 0 0 20px;
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;
}

/* ── Table ── */
.aks-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.aks-table thead th {
  text-align: left;
  padding: 10px 16px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.09em;
  color: rgb(120, 135, 155);
  background: rgb(248, 250, 253);
  border-bottom: 1px solid rgb(228, 235, 244);
  white-space: nowrap;
}

.aks-table tbody tr {
  border-bottom: 1px solid rgb(238, 244, 250);
  transition: background 0.12s;
}

.aks-table tbody tr:last-child {
  border-bottom: none;
}

.aks-table tbody tr:hover {
  background: rgb(250, 252, 255);
}

.aks-table td {
  padding: 12px 16px;
  vertical-align: middle;
}

.aks-row-revoked {
  opacity: 0.5;
}

.aks-cell-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.aks-key-name {
  font-weight: 500;
  color: var(--primary-navy);
}

.aks-badge-revoked {
  display: inline-block;
  padding: 1px 7px;
  font-size: 10.5px;
  font-weight: 600;
  border-radius: 10px;
  background: rgb(245, 247, 250);
  color: rgb(100, 116, 136);
  border: 1px solid rgb(210, 220, 232);
}

.aks-cell-key {
  white-space: nowrap;
}

.aks-prefix {
  font-family: 'SF Mono', 'Fira Mono', monospace;
  font-size: 12px;
  color: var(--primary-navy);
  background: rgb(246, 249, 253);
  padding: 3px 8px;
  border-radius: 5px;
  border: 1px solid rgb(218, 228, 240);
}

.aks-prefix-dots {
  letter-spacing: 0.05em;
  color: rgb(160, 175, 192);
}

.aks-cell-scopes {
  white-space: nowrap;
}

.aks-scope-chip {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  background: rgb(235, 243, 255);
  color: var(--primary-navy);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.aks-cell-meta {
  font-size: 12.5px;
  color: rgb(100, 116, 136);
  white-space: nowrap;
}

.aks-cell-action {
  text-align: right;
}

.aks-revoke-btn {
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  border: 1px solid rgb(210, 220, 232);
  background: #fff;
  color: #dc2626;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}

.aks-revoke-btn:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #fecaca;
}

.aks-revoke-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* ── Modal overlay ── */
.aks-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 20, 40, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.aks-modal {
  background: #fff;
  border-radius: 14px;
  padding: 28px 28px 24px;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(10, 20, 40, 0.22);
}

.aks-modal-sm {
  max-width: 400px;
}

.aks-modal-icon-wrap {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: rgb(235, 243, 255);
  color: var(--primary-navy);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.aks-modal-icon-wrap svg {
  width: 22px;
  height: 22px;
}

.aks-modal-icon-danger {
  background: #fef2f2;
  color: #dc2626;
}

.aks-modal-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--primary-navy);
  margin: 0 0 10px;
  text-align: center;
}

.aks-modal-body {
  font-size: 13.5px;
  color: rgb(70, 85, 105);
  margin: 0 0 18px;
  line-height: 1.6;
  text-align: center;
}

.aks-token-box {
  display: flex;
  align-items: stretch;
  background: rgb(246, 248, 252);
  border: 1px solid rgb(210, 220, 235);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 18px;
}

.aks-token-code {
  flex: 1;
  min-width: 0;
  padding: 11px 14px;
  font-family: 'SF Mono', 'Fira Mono', monospace;
  font-size: 11.5px;
  color: var(--primary-navy);
  word-break: break-all;
  line-height: 1.5;
}

.aks-copy-btn {
  padding: 11px 16px;
  background: var(--primary-navy);
  color: #fff;
  border: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.13s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.aks-copy-btn:hover {
  background: #1a2f52;
}

.aks-example-block {
  background: rgb(245, 248, 252);
  border: 1px solid rgb(218, 228, 240);
  border-radius: 7px;
  padding: 12px 14px;
  margin-bottom: 18px;
}

.aks-example-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: rgb(120, 135, 155);
  margin: 0 0 8px;
  text-transform: uppercase;
}

.aks-example-code {
  font-family: 'SF Mono', 'Fira Mono', monospace;
  font-size: 11px;
  color: var(--primary-navy);
  line-height: 1.7;
  display: block;
  word-break: break-all;
}

.aks-modal-done-btn {
  width: 100%;
  justify-content: center;
  padding: 11px;
  font-size: 14px;
}

.aks-modal-btns {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 4px;
}
</style>



// WEBPACK FOOTER //
// ApiKeysSettings.vue?1867e0b6